import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { requestSerialPort, webSerialSupported } from './esptool';
import {
  SerialSession,
  SpiffsEntry,
  provisionFilesOverSerial,
} from './serialSession';
import { t } from '../i18n';

interface UsbSessionValue {
  session: SerialSession | null;
  connected: boolean;
  log: string;
  files: SpiffsEntry[];
  busy: boolean;
  error: string | null;
  connect: () => Promise<SerialSession>;
  attach: (port: SerialPort) => Promise<SerialSession>;
  takePort: () => Promise<SerialPort | null>;
  disconnect: () => Promise<void>;
  refreshFiles: () => Promise<SpiffsEntry[]>;
  readFile: (path: string) => Promise<string>;
  writeFile: (path: string, content: string) => Promise<void>;
  removeFile: (path: string) => Promise<void>;
  sendLine: (line: string) => Promise<void>;
  provisionSettings: (settingsJson: string) => Promise<void>;
  clearError: () => void;
}

const UsbSessionContext = createContext<UsbSessionValue | null>(null);

export function UsbSessionProvider({ children }: { children: React.ReactNode }) {
  const sessionRef = useRef<SerialSession | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const [session, setSession] = useState<SerialSession | null>(null);
  const [log, setLog] = useState('');
  const [files, setFiles] = useState<SpiffsEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bind = useCallback((next: SerialSession) => {
    unsubRef.current?.();
    sessionRef.current = next;
    setSession(next);
    setLog(next.log);
    unsubRef.current = next.onData(() => setLog(next.log));
  }, []);

  const disconnect = useCallback(async () => {
    unsubRef.current?.();
    unsubRef.current = null;
    const current = sessionRef.current;
    sessionRef.current = null;
    setSession(null);
    setFiles([]);
    if (current) {
      await current.close();
    }
  }, []);

  const attach = useCallback(async (port: SerialPort) => {
    await disconnect();
    const next = new SerialSession();
    await next.attach(port);
    bind(next);
    return next;
  }, [bind, disconnect]);

  const takePort = useCallback(async () => {
    unsubRef.current?.();
    unsubRef.current = null;
    const current = sessionRef.current;
    sessionRef.current = null;
    setSession(null);
    setFiles([]);
    if (!current) {
      return null;
    }
    return current.stealPort();
  }, []);

  const connect = useCallback(async () => {
    if (!webSerialSupported()) {
      throw new Error(t('usb.needsHttps'));
    }
    const existing = sessionRef.current;
    if (existing?.connected) {
      return existing;
    }
    setBusy(true);
    setError(null);
    try {
      const port = await requestSerialPort();
      await disconnect();
      const next = new SerialSession();
      await next.open(port);
      bind(next);
      try {
        await next.waitUntilReady();
        const list = await next.listFiles('/');
        setFiles(list);
      } catch {
        // still connected; board may still be booting
      }
      return next;
    } catch (err: any) {
      const message = err?.message || String(err);
      setError(message);
      throw err;
    } finally {
      setBusy(false);
    }
  }, [bind, disconnect]);

  const withSession = useCallback(async <T,>(work: (session: SerialSession) => Promise<T>): Promise<T> => {
    const current = sessionRef.current;
    if (!current?.connected) {
      throw new Error(t('usb.notConnectedError'));
    }
    setBusy(true);
    setError(null);
    try {
      return await work(current);
    } catch (err: any) {
      const message = err?.message || String(err);
      setError(message);
      throw err;
    } finally {
      setBusy(false);
    }
  }, []);

  const refreshFiles = useCallback(async () => {
    return withSession(async (current) => {
      const list = await current.listFiles('/');
      setFiles(list);
      return list;
    });
  }, [withSession]);

  const readFile = useCallback((path: string) => withSession((current) => current.readFile(path)), [withSession]);
  const writeFile = useCallback(
    (path: string, content: string) => withSession((current) => current.writeFile(path, content)),
    [withSession],
  );
  const removeFile = useCallback(
    (path: string) => withSession(async (current) => {
      await current.removeFile(path);
      const list = await current.listFiles('/');
      setFiles(list);
    }),
    [withSession],
  );
  const sendLine = useCallback((line: string) => withSession((current) => current.sendLine(line)), [withSession]);
  const provisionSettings = useCallback(
    (settingsJson: string) => withSession(async (current) => {
      await provisionFilesOverSerial(current, settingsJson);
      const list = await current.listFiles('/');
      setFiles(list);
    }),
    [withSession],
  );

  const value = useMemo<UsbSessionValue>(() => ({
    session,
    connected: Boolean(session?.connected),
    log,
    files,
    busy,
    error,
    connect,
    attach,
    takePort,
    disconnect,
    refreshFiles,
    readFile,
    writeFile,
    removeFile,
    sendLine,
    provisionSettings,
    clearError: () => setError(null),
  }), [
    session,
    log,
    files,
    busy,
    error,
    connect,
    attach,
    takePort,
    disconnect,
    refreshFiles,
    readFile,
    writeFile,
    removeFile,
    sendLine,
    provisionSettings,
  ]);

  return (
    <UsbSessionContext.Provider value={value}>
      {children}
    </UsbSessionContext.Provider>
  );
}

export function useUsbSession(): UsbSessionValue {
  const value = useContext(UsbSessionContext);
  if (!value) {
    throw new Error('useUsbSession must be used inside UsbSessionProvider');
  }
  return value;
}
